import { HttpClient, HttpErrorResponse, HttpResponse, HttpParams } from "@angular/common/http";
import { Injectable, inject, Injector, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subject, Observable, catchError, finalize, map, of, shareReplay, tap } from "rxjs";
import { environment } from '../../environments/environment';
import { UserModel, GeneralSettingsModel, JwTokenModel, Permissions, PatientModel } from "../core/api/models";
import { AccountApiService, StaffApiService } from '../core/api/services';
import { PatientSessionService } from "../staff/home/patient-session.service";
import { AlertService } from '../shared/services/alert.service';
import { Utils } from '../core/utils';
import { PatientDataFacadeService } from '../core/services/patient-data-facade.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  accountApiService = inject(AccountApiService);

  public tokenInfo$: Subject<JwTokenModel | null> = new Subject<JwTokenModel | null>();
  public tokenInfo: JwTokenModel | null = null;


  public userInfo$: Subject<UserModel | null> = new Subject<UserModel | null>();
  public userInfo: UserModel | null = null;

  public myPatients$: Subject<PatientModel[] | null> = new Subject<PatientModel[] | null>();
  public myPatients: PatientModel[] | null = null;

  public generalSettings$: Subject<GeneralSettingsModel | null> = new Subject<GeneralSettingsModel | null>();
  public generalSettings: GeneralSettingsModel | null = null;

  private _authStateChanged: Subject<boolean> = new BehaviorSubject<boolean>(false);
  private _sessionConfirmVisible = signal<boolean>(false);
  private refreshTokenRequest$: Observable<JwTokenModel | null> | null = null;

  // Use private property for lazy injection
  private patientSessionServiceInstance: PatientSessionService | null = null;
  private patientDataFacadeServiceInstance: PatientDataFacadeService | null = null;

  // Create getter for PatientSessionService to avoid circular dependency
  private get patientSessionService(): PatientSessionService {
    if (!this.patientSessionServiceInstance) {
      this.patientSessionServiceInstance = this.injector.get(PatientSessionService);
    }
    return this.patientSessionServiceInstance;
  }

  private get patientDataFacadeService(): PatientDataFacadeService {
    if (!this.patientDataFacadeServiceInstance) {
      this.patientDataFacadeServiceInstance = this.injector.get(PatientDataFacadeService);
    }
    return this.patientDataFacadeServiceInstance;
  }

  public onStateChanged() {
    return this._authStateChanged.asObservable();
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector,
    private staffApiService: StaffApiService,
    private alertService: AlertService,
  ) {

    const tokenInfoData = localStorage.getItem('tokenInfo');
    if (tokenInfoData) {
      this.tokenInfo = JSON.parse(tokenInfoData) as JwTokenModel;
      console.info('Has Token Info');
      this.tokenInfo$.next(this.tokenInfo);
    } else {
      this.tokenInfo$.next(null);
    }

    const currentUserData = localStorage.getItem('userInfo');
    if (currentUserData) {
      this.userInfo = JSON.parse(currentUserData) as UserModel;
      console.info('Has User Info');
      this.userInfo$.next(this.userInfo);
    } else {
      this.userInfo$.next(null);
    }

    const currentMyPatientsData = localStorage.getItem('myPatients');
    if (currentMyPatientsData) {
      this.myPatients = JSON.parse(currentMyPatientsData) as PatientModel[];
      console.info('Has My Patients');
      this.myPatients$.next(this.myPatients);
    }
    const generalSettingsData = localStorage.getItem('generalSettings');
    if (generalSettingsData) {
      this.generalSettings = JSON.parse(generalSettingsData) as GeneralSettingsModel;
      console.info('Has General Settings');
      this.generalSettings$.next(this.generalSettings);
    } else {
      this.generalSettings$.next(null);
    }

    this.tokenInfo$.subscribe(x => {
      console.debug('tokenInfo$', x);
      if (x) {
        localStorage.setItem('tokenInfo', JSON.stringify(x));
      }
      else
        localStorage.removeItem('tokenInfo');
      this.tokenInfo = x;
    });

    this.userInfo$.subscribe(x => {
      console.debug('userInfo$', x);
      if (x) {
        localStorage.setItem('userInfo', JSON.stringify(x));
      }
      else
        localStorage.removeItem('userInfo');
      this.userInfo = x;
    });

    this.myPatients$.subscribe(x => {
      console.debug('myPatients$', x);
      if (x) {
        localStorage.setItem('myPatients', JSON.stringify(x));
      }
      else
        localStorage.removeItem('myPatients');
      this.myPatients = x;
    });

    this.generalSettings$.subscribe(x => {
      console.debug('generalSettings$', x);
      if (x) {
        localStorage.setItem('generalSettings', JSON.stringify(x));
      }
      else
        localStorage.removeItem('generalSettings');
      this.generalSettings = x;
    });
  }


  public signIn(email: string, password: string) {


    return this.accountApiService.Authenticate({ body: { username: email, password: password } })
      .pipe(tap(token => {
        if (token && token.access_token) {
          this.tokenInfo$.next(token);
          this._authStateChanged.next(true);
        } else {
          this.tokenInfo$.next(null);
          this._authStateChanged.next(false);
        }
        return;
      },
        error => {
          console.log(error);
          //this.messageService.add({ severity: 'error', detail: 'Internal Error: ' + (error.message || error) });
        }));
  }


  // sign out
  public signOut() {
    return this.signOutNoRedirect().pipe(
      tap((success) => {
        this.router.navigate(['/auth/signin']);
      },
        error => {          
        this.router.navigate(['/auth/signin']);
        })
    );
  }

  public signOutNoRedirect() {

    this._authStateChanged.next(false);
    const currenttokenInfo = JSON.parse(localStorage.getItem('tokenInfo') || '{}');

    if (this.isSignedIn()) {
      this.accountApiService.Logout()
        .forEach(() => { })
        .catch(err => { })
        .finally(() => { });
    }

    this.tokenInfo$.next(null);
    this.userInfo$.next(null);
    this.myPatients$.next(null);
    this.patientDataFacadeService.clearAll();
    this._authStateChanged.next(false);

    return of(true);

  }



  public isSignedIn(): boolean {


    if (this.tokenInfo != null
      && this.tokenInfo.expires_at != null
      && new Date(this.tokenInfo.expires_at) > new Date()) {
      return true;
    }
    return false;
  }

  public hasUsableRefreshToken(): boolean {
    return !!(
      this.tokenInfo?.refresh_token &&
      this.tokenInfo?.refresh_expires_at &&
      new Date(this.tokenInfo.refresh_expires_at) > new Date()
    );
  }


  public redirectAfterLogin(userInfo?: UserModel | null, returnUrl?: string | undefined | null) {
    // replaceUrl drops /auth/signin?token= from history so WKWebView reloads
    // (and WebContent process recovery) do not reuse a spent one-time handoff token.
    const navExtras = { replaceUrl: true };
    userInfo = userInfo || this.userInfo;
    if (userInfo == null || userInfo == undefined)
      this.router.navigate(['/auth/signin'], navExtras);
    else if (returnUrl && returnUrl != '' && returnUrl != '/' && returnUrl != '/auth/signin') {
      const normalizedReturnUrl = returnUrl.startsWith('/')
        ? returnUrl
        : `/${returnUrl}`;
      if (
        normalizedReturnUrl.startsWith('//') ||
        normalizedReturnUrl.toLowerCase().includes('://')
      ) {
        this.router.navigate(['/select-patient'], navExtras);
        return;
      }
      void this.router.navigateByUrl(normalizedReturnUrl, navExtras);
    }
    //TODO: remove this and change to redirect to the home page
    else {
      //check if there is any patient on the patientSession 
      if (this.patientSessionService.getPatientID()) {
        if (userInfo.Permissions != null && userInfo.Permissions.indexOf(Permissions.SparkApp_Supervisor) >= 0) {
          this.router.navigate(['/supervisor/' + this.patientSessionService.getPatientID() + '/overview'], navExtras);
        }
        else {
          this.router.navigate(['/staff/' + this.patientSessionService.getPatientID() + '/sessions'], navExtras);
        }
      }
      else {
        this.router.navigate(['/select-patient'], navExtras);
      }
    }

  }

  public hasPermission(permission: Permissions): boolean {
    return this.userInfo?.Permissions?.includes(permission) ?? false;
  }

  public canEditBip(): boolean {
    return this.hasPermission(Permissions.SparkApp_EditBIP);
  }

  public hideSessionConfirm() {
    this._sessionConfirmVisible.set(false);
    this.alertService.clear();
  }

  public showSessionConfirm() {
    if (this._sessionConfirmVisible()) {
      return;
    }
    this.alertService.confirm("Session Expiring", "You have been inactive for 25 minutes. Would you like to continue? If no response is given you will be logged out.").subscribe(x => {
      if (x) {
        
        if (this.isSignedIn()
          && this.tokenInfo?.refresh_token
          && this.tokenInfo?.refresh_expires_at
          && new Date(this.tokenInfo.refresh_expires_at) > new Date()) {

          const refreshingToken = localStorage.getItem('refreshingToken') == this.tokenInfo?.expires_at;
          if (!refreshingToken) {
            localStorage.setItem('refreshingToken', this.tokenInfo!.expires_at!);
            this.getTokenInfo().forEach(token => {
              console.debug('Token Refreshed', token?.expires_at);
              localStorage.removeItem('refreshingToken');
            });
          }
        }
        this._sessionConfirmVisible.set(false);
      }
      else {        
        this.signOut().subscribe();
        this._sessionConfirmVisible.set(false);
      }
    });

    Utils.beep();
    this._sessionConfirmVisible.set(true);    
  }


  private _gettingTokenInfo = false;
  public getTokenInfo(): Observable<JwTokenModel | null> {

    this._gettingTokenInfo = true;
    var refresh_token: string | undefined = undefined;


    if (this.tokenInfo != null
      && this.tokenInfo.refresh_token != null
      && this.tokenInfo.refresh_expires_at != null
      && new Date(this.tokenInfo.refresh_expires_at) > new Date())
      refresh_token = this.tokenInfo.refresh_token!;
    if (refresh_token) {
      return this.accountApiService.RefreshToken({ body: { refresh_token: refresh_token } })

        .pipe(tap(
          (result) => {
            this._gettingTokenInfo = false;
            this.tokenInfo$.next(result);
          },
          (err) => {
            this._gettingTokenInfo = false;
            this.userInfo$.next(null);
            this.myPatients$.next(null);
            this.tokenInfo$.next(null);
            return of(null);
          }));
    }
    this._gettingTokenInfo = false;
    return of(null);
  }

  public refreshTokenOnce(): Observable<JwTokenModel | null> {
    if (this.refreshTokenRequest$) {
      return this.refreshTokenRequest$;
    }

    if (!this.hasUsableRefreshToken()) {
      return of(null);
    }

    const refreshToken = this.tokenInfo?.refresh_token;
    if (!refreshToken) {
      return of(null);
    }

    this.refreshTokenRequest$ = this.accountApiService.RefreshToken({
      body: { refresh_token: refreshToken },
    }).pipe(
      tap((result) => {
        this.tokenInfo$.next(result);
      }),
      catchError((error) => {
        console.warn('Token refresh failed', error);
        return of(null);
      }),
      finalize(() => {
        this.refreshTokenRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshTokenRequest$;
  }

  public token() {
    if (this.tokenInfo) {
      return of(this.tokenInfo);
    }
    if (this._gettingTokenInfo)
      return this.tokenInfo$.asObservable();

    return this.getTokenInfo().pipe(
      map((tokenInfo) => {
        if (tokenInfo) {
          return tokenInfo;
        }
        return null;
      }));
  }


  private _gettingUserInfo = false;
  public getUserInfo(): Observable<UserModel | null> {


    this._gettingUserInfo = true;
    return this.accountApiService.Me()

      .pipe(tap(
        (result) => {
          this._gettingUserInfo = false;
          this.userInfo$.next(result);
        },
        (err) => {
          this._gettingUserInfo = false;
          this.userInfo$.next(null);
          return of(null);
        }));
  }

  public user() {
    if (this.userInfo) {
      return of(this.userInfo);
    }
    if (this._gettingUserInfo)
      return this.userInfo$.asObservable();

    return this.getUserInfo().pipe(
      map((userInfo) => {
        if (userInfo) {
          return userInfo;
        }
        return null;
      }));
  }

  public getMyPatients() {
    if (this.myPatients) {
      return of(this.myPatients);
    }
    if (this._gettingMyPatients)
      return this.myPatients$.asObservable();

    this._gettingMyPatients = true;
    return this.staffApiService.GetMyPatients()
      .pipe(tap(
        (result) => {
          this._gettingMyPatients = false;
          this.myPatients$.next(result);
        },
        (err) => {
          this._gettingMyPatients = false;
          this.myPatients$.next(null);
          return of(null);
        }
      ));
  }
  private _gettingMyPatients = false;


  private _gettingSettings = false;
  public getSettings(): Observable<GeneralSettingsModel | null> {

    this._gettingSettings = true;
    return this.accountApiService.GeneralSettings()

      .pipe(tap(
        (result) => {
          this._gettingSettings = false;
          this.generalSettings$.next(result);
        },
        (err) => {
          this._gettingSettings = false;
          this.generalSettings$.next(null);
          return of(null);
        }));
  }

  public settings() {
    if (this.generalSettings) {
      return of(this.generalSettings);
    }
    if (this._gettingSettings)
      return this.generalSettings$.asObservable();
    return this.getSettings().pipe(
      map((generalSettings) => {
        if (generalSettings) {
          return generalSettings;
        }
        return null;
      }));
  }



  public getClientFromUrl() {
    const url = window.location.hostname.split('.')[0].toLowerCase();
    if (url.startsWith('spark-'))
      return url.substring(6);
    return url;
  }

  public getOptoURL() {

    var host = window.location.hostname.toLowerCase();

    if (host.startsWith('spark-'))
      return 'https://' + host.substring(6);
    if (host == 'localhost')
      return 'https://localhost:44354/';
    if (environment.production && this.generalSettings?.SiteName)
      return `https://${this.generalSettings?.SiteName}.optomiser.com`;
    return null;
  }


  /*
  public setCookie(cname: string, cvalue: string, exdays: number) {
    var expires = "";
    if (exdays) {
      var d = new Date();
      d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
      expires = ";expires=" + d.toUTCString();
    }
    document.cookie = cname + "=" + cvalue + expires + ";path=/";
  }

  public getCookie(cname: string) {
    var name = cname + "=";
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }
  */


}
